import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PrimaryButton, PageHeader, TitleRow, IconButton,
} from '../ui/common';
import { ArrowLeftIcon, DownloadIcon, SheetIcon, CameraIcon, InfoIcon, LockIcon } from '../ui/icons';
import { Banner } from '../components/ui/Banner';
import { useKontoWPrzerwie } from '../components/przerwa/KontoWPrzerwie';
import { API_BASE } from '../config';

// Eksport całej firmy (FAZA F, F.12). Dwa kroki, jak na serwerze (routes/eksportFirmy.js):
// POST z tokenem CSRF daje jednorazowy adres, a pobranie idzie zwykłym linkiem, więc przeglądarka
// zapisuje archiwum prosto na dysk. Pobranie przez fetch i Blob trzymałoby w pamięci karty całe
// archiwum, razem z gigabajtami zdjęć. Przycisk działa także w przerwie konta.

function csrfHeader() {
  return { 'X-CSRF-Token': localStorage.getItem('csrfToken') || '' };
}

export function rozpocznijPobieranie(adres) {
  const a = document.createElement('a');
  a.href = adres;
  // Pusta wartość: nazwę pliku podaje serwer (Content-Disposition).
  a.setAttribute('download', '');
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function EksportFirmy() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { wPrzerwie, powod: powodOdczytu } = useKontoWPrzerwie();
  const [stan, setStan] = useState('gotowy'); // gotowy | przygotowuje | rozpoczete
  const [blad, setBlad] = useState('');
  const administrator = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';

  const komunikatBledu = (kod) => {
    switch (kod) {
      case 'export_in_progress': return t('companyExport.error.inProgress');
      case 'export_busy': return t('companyExport.error.busy');
      case 'export_limit': return t('companyExport.error.limit');
      case 'admin_required': return t('companyExport.adminOnly');
      default: return t('companyExport.error.generic');
    }
  };

  const pobierz = async () => {
    setBlad('');
    setStan('przygotowuje');
    try {
      const res = await fetch(`${API_BASE}/api/organization-export`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeader(),
      });
      const dane = await res.json().catch(() => ({}));
      if (!res.ok || !dane.url) {
        setBlad(komunikatBledu(dane.code));
        setStan('gotowy');
        return;
      }
      rozpocznijPobieranie(`${API_BASE}${dane.url}`);
      setStan('rozpoczete');
    } catch (_) {
      setBlad(t('companyExport.error.generic'));
      setStan('gotowy');
    }
  };

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('companyExport.title')}</Title>
            <Subtitle>{t('companyExport.subtitle')}</Subtitle>
          </div>
        </TitleRow>
      </PageHeader>

      <Karta data-cy="company-export">
        <Naglowek>{t('companyExport.contentsTitle')}</Naglowek>
        <Lista>
          <Pozycja><Znak aria-hidden="true"><SheetIcon size={18} /></Znak>{t('companyExport.item.data')}</Pozycja>
          <Pozycja><Znak aria-hidden="true"><DownloadIcon size={18} /></Znak>{t('companyExport.item.invoicesPdf')}</Pozycja>
          <Pozycja><Znak aria-hidden="true"><CameraIcon size={18} /></Znak>{t('companyExport.item.photos')}</Pozycja>
          <Pozycja data-cy="company-export-item-settings"><Znak aria-hidden="true"><SheetIcon size={18} /></Znak>{t('companyExport.item.settings')}</Pozycja>
          <Pozycja><Znak aria-hidden="true"><InfoIcon size={18} /></Znak>{t('companyExport.item.readme')}</Pozycja>
        </Lista>

        <Naglowek>{t('companyExport.howTitle')}</Naglowek>
        <Opis>{t('companyExport.how.time')}</Opis>
        <Opis>{t('companyExport.how.limit')}</Opis>
        {wPrzerwie && (
          <Opis data-cy="company-export-paused">
            {powodOdczytu === 'rezygnacja' ? t('rezygnacja.eksport.opis') : t('companyExport.how.paused')}
          </Opis>
        )}

        <Banner tone="warning" compact icon={<LockIcon size={16} />} style={{ margin: 'var(--sp-4) 0' }}>
          {t('companyExport.privacy')}
        </Banner>

        {!administrator && (
          <Banner tone="info" compact data-cy="company-export-admin-only" style={{ marginBottom: 'var(--sp-4)' }}>
            {t('companyExport.adminOnly')}
          </Banner>
        )}
        {blad && (
          <Banner tone="danger" compact data-cy="company-export-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {blad}
          </Banner>
        )}
        {stan === 'rozpoczete' && !blad && (
          <Banner tone="success" compact data-cy="company-export-started" style={{ marginBottom: 'var(--sp-4)' }}>
            {t('companyExport.started')}
          </Banner>
        )}

        <PrimaryButton
          type="button"
          data-cy="company-export-download"
          onClick={pobierz}
          disabled={!administrator || stan === 'przygotowuje'}
          aria-busy={stan === 'przygotowuje' || undefined}
        >
          <DownloadIcon size={18} />
          {stan === 'przygotowuje' ? t('companyExport.preparing') : t('companyExport.download')}
        </PrimaryButton>
      </Karta>
    </PanelContainer>
  );
}

const Karta = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  padding: clamp(18px, 2.4vw, 28px);
  max-width: 760px;
`;

const Naglowek = styled.h2`
  margin: 0 0 var(--sp-3);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);

  &:not(:first-child) {
    margin-top: var(--sp-5);
  }
`;

const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--sp-2);
`;

const Pozycja = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  color: var(--text);
  line-height: 1.5;
`;

const Znak = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  color: var(--primary);
  padding-top: 2px;
`;

const Opis = styled.p`
  margin: 0 0 var(--sp-2);
  color: var(--text-muted);
  line-height: 1.55;
`;
